import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Card } from '@atlaskit/media-card';
import type { FileIdentifier } from '@atlaskit/media-client';
import { ButtonItem } from '@atlaskit/menu';
import { Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import { useOnClickOutsideRef } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseAttachment } from '@databases/types/database';
import { getMediaCollectionId } from '@databases/utils/media-client';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import React, { FunctionComponent, Suspense, useEffect, useRef, useState } from 'react';
import { DropzoneRef } from 'react-dropzone';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { dynamicImport } from '@canvas/dynamic-import';

import { useMediaApiFileType } from '../attachments';
import { useMediaClient } from '../media-client';

const MediaViewer = React.lazy(() => dynamicImport(() => import('./MediaViewer')));

const ButtonContainer = styled.div<{
  isOpen: boolean;
}>`
  position: absolute;
  top: 13px;
  right: 11px;
  transition: opacity 0.2s;
  opacity: ${(props) => (props.isOpen ? '1' : '0')};

  button {
    background-color: ${(props) =>
      props.isOpen ? '' : token('color.background.accent.gray.subtlest')};

    &:hover {
      background-color: ${(props) =>
        props.isOpen ? '' : token('color.background.accent.gray.subtlest')};
    }
  }
`;

const UploadingSpinnerContainerStyles = xcss({
  width: '100%',
  height: '100%',
});

interface MediaViewerType {
  dropzoneRef?: React.RefObject<DropzoneRef>;
  contentId: string;
  value: (
    | DatabaseAttachment
    | File
    | {
        src: string;
      }
    | {
        fileId: string;
      }
    | undefined
  ) & {
    _id: string;
  };
  handleDelete?: () => void;
  showSpinner?: boolean;
  mediaFiles?: FileIdentifier[];
  renderButtons?: boolean;
}

const AttachmentViewer: FunctionComponent<MediaViewerType> = ({
  dropzoneRef,
  contentId,
  value,
  handleDelete,
  showSpinner,
  mediaFiles,
  renderButtons = true,
}) => {
  const [mediaCollectionId, setMediaCollectionId] = useState<null | string>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { formatMessage } = useIntl();
  const mediaClient = useMediaClient(contentId);
  const [file, setFile] = useState<null | FileIdentifier>(null);
  const menuRef = useRef<HTMLElement>(null);

  const store = defaultStoreUtils.useStore();

  const attachmentData = useMediaApiFileType(
    typeof value === 'object' && 'fileId' in value ? value : undefined,
    contentId
  );

  const handleViewInFullscreen = () => {
    if (value && 'fileId' in value && mediaCollectionId) {
      setIsOpen(false);
      setFile({
        mediaItemType: 'file',
        id: value.fileId,
        collectionName: mediaCollectionId,
      });
    }
  };

  useOnClickOutsideRef(menuRef.current, true, () => {
    setIsOpen(false), true;
  });

  useEffect(() => {
    const getCollectionId = async () => {
      const id = await getMediaCollectionId(store.getState(), contentId);
      if (id) {
        setMediaCollectionId(id);
      }
    };

    getCollectionId();
  }, [contentId, store]);

  if (showSpinner) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        xcss={UploadingSpinnerContainerStyles}
      >
        <Spinner />
      </Flex>
    );
  }

  if (attachmentData?.status !== AsyncOperationStatus.SUCCEEDED) {
    return null;
  }

  if (
    file &&
    mediaFiles &&
    mediaCollectionId &&
    mediaClient &&
    mediaClient.mediaClientConfig
  ) {
    return (
      <Suspense fallback={<Spinner />}>
        <MediaViewer
          mediaClientConfig={mediaClient.mediaClientConfig}
          selectedItem={file}
          items={mediaFiles}
          collectionName={mediaCollectionId}
          onClose={() => setFile(null)}
        />
      </Suspense>
    );
  }

  return (
    attachmentData &&
    mediaClient &&
    mediaCollectionId &&
    'fileId' in value && (
      <>
        <Card
          mediaClientConfig={mediaClient.mediaClientConfig}
          dimensions={{ width: '250px', height: '250px' }}
          disableOverlay={
            attachmentData.data === 'image' ||
            attachmentData.data === 'audio' ||
            attachmentData.data === 'video'
          }
          shouldHideTooltip
          useInlinePlayer
          resizeMode="fit"
          identifier={{
            mediaItemType: 'file',
            id: value.fileId,
            collectionName: mediaCollectionId,
          }}
        />
        {renderButtons ? (
          <ButtonContainer
            className="attachment-actions-container"
            isOpen={isOpen}
            ref={menuRef}
          >
            <DropdownMenu<HTMLButtonElement>
              isOpen={isOpen}
              placement="bottom-end"
              trigger={({ triggerRef, ...props }) => (
                <IconButton
                  {...props}
                  icon={MoreIcon}
                  label=""
                  ref={triggerRef}
                  isSelected={isOpen}
                  onClick={() => setIsOpen(!isOpen)}
                />
              )}
              shouldRenderToParent
            >
              <DropdownItemGroup>
                <ButtonItem onClick={handleViewInFullscreen}>
                  {formatMessage(messages['media.full-screen-view'])}
                </ButtonItem>
                <ButtonItem
                  onClick={() => {
                    setIsOpen(false);
                    if (dropzoneRef?.current) {
                      // Open dropzone file dialog and handle change
                      dropzoneRef.current.open();
                    }
                  }}
                >
                  {formatMessage(messages['media.change-file'])}
                </ButtonItem>
                <ButtonItem
                  onClick={() => {
                    setIsOpen(false);
                    if (handleDelete) {
                      handleDelete();
                    }
                  }}
                >
                  {formatMessage(messages['media.delete'])}
                </ButtonItem>
              </DropdownItemGroup>
            </DropdownMenu>
          </ButtonContainer>
        ) : null}
      </>
    )
  );
};

export default AttachmentViewer;
